import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import {
  arrayOf, bool, exact, func, number, string,
} from 'prop-types';
import {
  Flex, Typography, Icon, Loader,
} from '@atoms';
import { Header, Search, ErrorToast } from '@molecules';
import { AvailabilityProcessModal } from '@organisms';
import { ReactComponent as PlusIcon } from '@assets/plus.svg';
import { ReactComponent as EditIcon } from '@assets/edit.svg';

const AvailabilitiesList = ({
  onStartDateRemove,
  onEndDateRemove,
  onStartTimeRemove,
  onEndTimeRemove,
  isConfirmButtonDisabled,
  onAvailabilityDeleteModalChangeState,
  onAvailabilityEdit,
  onAvailabilityModalChangeState,
  startTime,
  endTime,
  startDate,
  endDate,
  onStartTimeChange,
  onEndTimeChange,
  onStartDateChange,
  onEndDateChange,
  onDayClick,
  selectedWeekDays,
  onAvailabilityAdd,
  availabilities,
  availabilitiesForSearch,
  error,
  isLoading,
  onBackUpClick,
  onToastClose,
  onSearch,
  onAvailabilityNameChange,
  isAvailabilityEditing,
  onAvailabilityEditSubmit,
  availabilityName,
  isAvailabilityModalOpen,
  isAvailabilityDeleteModalOpen,
  onAvailabilityDelete,
  onAvailabilityClick,
}) => {
  const memoizedAvailabilitiesList = useMemo(() => availabilities.map(availability => (
    <>
      <AvailavilitiesListItem
        marginTop="lg"
        paddingBottom="lg"
        paddingRight="sm"
        row
        spaceBetween
        itemsCenter
        key={`availability-list-item-${availability.id}`}
        onClick={onAvailabilityClick(availability.id)}
      >
        <ConstrainedSizeTextWrapper
          width="90%"
          column
          justifyStart
          itemsStart
          shrink={0}
        >
          <Typography size="xl" weight="medium">{availability.name}</Typography>
        </ConstrainedSizeTextWrapper>
        <SizedFlex
          onClick={event => onAvailabilityEdit(event, availability.id)}
          width="10%"
          row
          justifyEnd
          itemsStart
          shrink={0}
        >
          <IconEdit SVG={EditIcon} width="20px" height="20px" />
        </SizedFlex>
      </AvailavilitiesListItem>
    </>
  )), [availabilities]);

  return (
    <>
      <SizedFlex width="100%" column justifyCenter itemsCenter>
        <Header title="Add Availability" onBackUpClick={onBackUpClick} />
        <Search listItems={availabilitiesForSearch} onSearch={onSearch} />
        <SizedFlex width="100%" column justifyStart itemsCenter paddingLeft="md" paddingRight="md">
          <AvailavilitiesListItem
            marginTop="lg"
            paddingBottom="lg"
            row
            justifyStart
            itemsCenter
            onClick={onAvailabilityAdd}
            key="create-new-availability-button"
          >
            <IconContainer grow={0} marginLeft="xl" marginRight="xl4" padding="xxs">
              <Icon SVG={PlusIcon} width="20px" height="20px" />
            </IconContainer>
            <Typography size="xl" weight="medium">
              Add New Availability
            </Typography>
          </AvailavilitiesListItem>
          {memoizedAvailabilitiesList}
        </SizedFlex>
      </SizedFlex>
      <AvailabilityProcessModal
        onStartDateRemove={onStartDateRemove}
        onEndDateRemove={onEndDateRemove}
        onStartTimeRemove={onStartTimeRemove}
        onEndTimeRemove={onEndTimeRemove}
        isConfirmButtonDisabled={isConfirmButtonDisabled}
        onAvailabilityDeleteModalChangeState={onAvailabilityDeleteModalChangeState}
        startTime={startTime}
        endTime={endTime}
        startDate={startDate}
        endDate={endDate}
        onStartTimeChange={onStartTimeChange}
        onEndTimeChange={onEndTimeChange}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onAvailabilityNameChange={onAvailabilityNameChange}
        isEditing={isAvailabilityEditing}
        onAvailabilityEditSubmit={onAvailabilityEditSubmit}
        availabilityName={availabilityName}
        isModalOpen={isAvailabilityModalOpen}
        onModalClose={onAvailabilityModalChangeState}
        isAvailabilityDeleteModalOpen={isAvailabilityDeleteModalOpen}
        onAvailabilityDelete={onAvailabilityDelete}
        onDayClick={onDayClick}
        selectedWeekDays={selectedWeekDays}
      />
      {isLoading && <Loader />}
      {error && <ErrorToast errorMessage={error} onToastClose={onToastClose} />}
    </>
  );
};

const SizedFlex = styled(Flex)`
  ${({ width }) => css`
    width: ${width};
    max-width: ${width};
  `}
`;

const IconEdit = styled(Icon)`
  ${({ theme: { color } }) => css`
    path {
      fill: ${color.black};
    }
  `}
`;

const AvailavilitiesListItem = styled(Flex)`
  ${({ theme: { color } }) => css`
    width: 100%;
    border-bottom: 1px solid ${color.warmGrey};
  `}
`;

const IconContainer = styled(Flex)`
  ${({ theme: { border, color } }) => css`
    border-radius: ${border.radius.circular};
    background-color: ${color.grannySmith};
  `}
`;

const ConstrainedSizeTextWrapper = styled(SizedFlex)`
  ${({ width }) => css`
    ${Typography} {
      max-width: ${width};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `}
`;

AvailabilitiesList.defaultProps = {
  error: null,
};

AvailabilitiesList.propTypes = {
  onStartDateRemove: func.isRequired,
  onEndDateRemove: func.isRequired,
  onStartTimeRemove: func.isRequired,
  onEndTimeRemove: func.isRequired,
  onAvailabilityClick: func.isRequired,
  isConfirmButtonDisabled: bool.isRequired,
  onAvailabilityEdit: func.isRequired,
  onAvailabilityModalChangeState: func.isRequired,
  onAvailabilityDeleteModalChangeState: func.isRequired,
  startTime: string.isRequired,
  endTime: string.isRequired,
  startDate: string.isRequired,
  endDate: string.isRequired,
  onStartTimeChange: func.isRequired,
  onEndTimeChange: func.isRequired,
  onStartDateChange: func.isRequired,
  onEndDateChange: func.isRequired,
  onDayClick: func.isRequired,
  selectedWeekDays: arrayOf(number).isRequired,
  onAvailabilityAdd: func.isRequired,
  onAvailabilityNameChange: func.isRequired,
  isAvailabilityEditing: bool.isRequired,
  onAvailabilityEditSubmit: func.isRequired,
  availabilityName: string.isRequired,
  isAvailabilityModalOpen: bool.isRequired,
  isAvailabilityDeleteModalOpen: bool.isRequired,
  onAvailabilityDelete: func.isRequired,
  availabilities: arrayOf(exact({
    name: string,
    id: number,
  })).isRequired,
  availabilitiesForSearch: arrayOf(exact({
    name: string,
    id: number,
  })).isRequired,
  error: string,
  isLoading: bool.isRequired,
  onBackUpClick: func.isRequired,
  onToastClose: func.isRequired,
  onSearch: func.isRequired,
};

export default AvailabilitiesList;
